<template>
  <section>
    <b-form-group
      class="mb-2"
    >
      <legend class="col-form-label pt-0">
        {{ $tk('ClientPortal.Ad.UrlBuilder.Url') }}
        <GjIcon
          v-b-tooltip.hover.bottom="$tk('ClientPortal.Ad.UrlBuilder.UrlInfo')"
          name="Info"
        />
      </legend>
      <b-form-input
        v-model="temporaryUrl"
        type="text"
        placeholder="https://www.example.com/page"
        :disabled="isProductAd"
        @input="prepareUtmUrl"
      />
    </b-form-group>
    <b-form-group
      class="mb-2"
    >
      <legend class="col-form-label pt-0">
        {{ $tk('ClientPortal.Ad.UrlBuilder.CampaignSource') }}
        <GjIcon
          v-b-tooltip.hover.bottom="$tk('ClientPortal.Ad.UrlBuilder.CampaignSourceInfo')"
          name="Info"
        />
      </legend>
      <v-select
        v-model="utmBuilder.utm_source"
        :options="options"
        label="text"
        taggable
        select-on-tab
        @input="prepareUtmUrl"
      />
    </b-form-group>
    <b-form-group
      class="mb-2"
    >
      <legend class="col-form-label pt-0">
        {{ $tk('ClientPortal.Ad.UrlBuilder.CampaignMedium') }}
        <GjIcon
          v-b-tooltip.hover.bottom="$tk('ClientPortal.Ad.UrlBuilder.CampaignMediumInfo')"
          name="Info"
        />
      </legend>
      <v-select
        v-model="utmBuilder.utm_medium"
        :options="options"
        label="text"
        taggable
        select-on-tab
        @input="prepareUtmUrl"
      />
    </b-form-group>
    <b-form-group
      class="mb-2"
    >
      <legend class="col-form-label pt-0">
        {{ $tk('ClientPortal.Ad.UrlBuilder.CampaignName') }}
        <GjIcon
          v-b-tooltip.hover.bottom="$tk('ClientPortal.Ad.UrlBuilder.CampaignNameInfo')"
          name="Info"
        />
      </legend>
      <v-select
        v-model="utmBuilder.utm_campaign"
        :options="options"
        label="text"
        taggable
        select-on-tab
        @input="prepareUtmUrl"
      />
    </b-form-group>
    <b-form-group
      class="mb-2"
    >
      <legend class="col-form-label pt-0">
        {{ $tk('ClientPortal.Ad.UrlBuilder.CampaignContent') }}
        <GjIcon
          v-b-tooltip.hover.bottom="$tk('ClientPortal.Ad.UrlBuilder.CampaignContentInfo')"
          name="Info"
        />
      </legend>
      <v-select
        v-model="utmBuilder.utm_content"
        :options="options"
        label="text"
        taggable
        select-on-tab
        @input="prepareUtmUrl"
      />
    </b-form-group>
    <template v-if="utmBuilder.customParameters.length > 0">
      <b-card-title>{{ $tk('ClientPortal.Ad.UrlBuilder.CustomParameters') }}</b-card-title>
      <hr>
      <b-form-row
        v-for="(customParameter, index) in utmBuilder.customParameters"
        :key="index"
      >
        <b-col sm="6">
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Ad.UrlBuilder.ParameterName')"
          >
            <b-form-input
              v-model="customParameter.name"
              type="text"
              @input="prepareUtmUrl"
            />
          </b-form-group>
        </b-col>
        <b-col sm="5">
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Ad.UrlBuilder.Value')"
          >
            <b-form-input
              v-model="customParameter.value"
              type="text"
              @input="prepareUtmUrl"
            />
          </b-form-group>
        </b-col>
        <b-col
          sm="1"
          class="pt-2 text-center"
        >
          <b-button
            class="mt-1"
            variant="link"
            size="sm"
            @click="removeCustomParameter(index)"
          >
            <GjIcon
              class="mr-50"
              name="Delete"
            />
          </b-button>
        </b-col>
      </b-form-row>
    </template>
    <b-button
      class="bg-white mb-50"
      variant="outline-primary"
      size="sm"
      @click="addNewCustomParameter"
    >
      <GjIcon
        class="mr-50"
        name="Plus"
      />
      {{ $tk('ClientPortal.Ad.UrlBuilder.AddParameter') }}
    </b-button>
    <hr>
  </section>
</template>

<script>
import vSelect from 'vue-select'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notification from '@/mixins/toastification'

export default {
  components: {
    vSelect,
    GjIcon,
  },
  mixins: [notification],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    options: Array,
    isProductAd: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      predefinedOptions: [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_content',
      ],
      utmBuilder: {
        customParameters: [],
      },
      temporaryUrl: 'https://',
      utmUrlPreview: '',
    }
  },
  watch: {
    // eslint-disable-next-line func-names
    '$attrs.value': function () {
      this.setUrlUtmContent()
    },
  },
  mounted() {
    this.setUrlUtmContent()
  },
  methods: {
    addNewCustomParameter() {
      const customParameter = {
        name: '',
        value: '',
      }

      this.utmBuilder.customParameters.push(customParameter)
    },
    removeCustomParameter(index) {
      this.utmBuilder.customParameters.splice(index, 1)
      this.prepareUtmUrl(this.$attrs.value)
    },
    resetUrlBuilder() {
      this.temporaryUrl = ''
      this.utmBuilder.customParameters = []
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.predefinedOptions.length; i++) {
        const parameterObject = { text: '' }
        this.$set(this.utmBuilder, this.predefinedOptions[i], parameterObject)
      }
    },
    setUtmParameter(firstParameter, secondParameter) {
      if (firstParameter === '') return

      if (this.predefinedOptions.filter(x => x === firstParameter).length > 0) {
        this.addPredefinedUtmParameter(firstParameter, secondParameter)
      } else {
        this.addCustomUtmParameter(firstParameter, secondParameter)
      }
    },
    addPredefinedUtmParameter(firstParameter, secondParameter) {
      var parameterObject = { text: secondParameter }
      this.$set(this.utmBuilder, firstParameter, parameterObject)
    },
    addCustomUtmParameter(firstParameter, secondParameter) {
      if (secondParameter === null) return

      const utmParameterExists = this.utmBuilder.customParameters.filter(x => x.name === firstParameter).length > 0
      if (utmParameterExists) return

      const customParameterObject = {
        name: firstParameter,
        value: secondParameter,
      }
      this.utmBuilder.customParameters.push(customParameterObject)
    },
    setUrlUtmContent() {
      var url = this.$attrs.value
      this.resetUrlBuilder()

      if (url === '') return

      url = this.checkUrl(url)

      const urlInfo = new URL(url)
      if (urlInfo.pathname !== '/') {
        this.temporaryUrl = urlInfo.origin + urlInfo.pathname + urlInfo.hash
      } else {
        this.temporaryUrl = urlInfo.origin + urlInfo.hash
      }
      const urlParameters = urlInfo.search.split('&')
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < urlParameters.length; i++) {
        if (i === 0) {
          urlParameters[0] = urlParameters[0].replace('?', '')
        }

        const parameterInfo = urlParameters[i].split('=')
        this.setUtmParameter(parameterInfo[0], parameterInfo[1])
      }

      if (this.isProductAd) this.temporaryUrl = decodeURI(this.temporaryUrl.replace('https://', ''))
    },
    validUrl(str) {
      if (this.isProductAd) return true

      const pattern = new RegExp('^(https?:\\/\\/)?' // protocol
                    + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
                    + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ip (v4) address
                    + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
                    + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
                    + '(\\#[-a-z\\d_]*)?$', 'i') // fragment locator
      return !!pattern.test(str)
    },
    checkUrl(url) {
      if (!/^https?:\/\//i.test(url)) {
        url = `https://${url}`
      }
      return url
    },
    changeInputValue() {
      this.$emit('input', this.utmUrlPreview)
    },
    prepareUtmUrl() {
      if (this.temporaryUrl === '') return

      // Need to check for valid url otherwise it will throw error cause of invalid url
      const validUrl = this.validUrl(this.temporaryUrl.trim())
      // eslint-disable-next-line consistent-return
      if (!validUrl) return ''

      this.temporaryUrl = this.checkUrl(this.temporaryUrl)
      const url = new URL(this.temporaryUrl)
      if (this.utmBuilder.utm_source != null && this.utmBuilder.utm_source.text !== '') {
        url.searchParams.set('utm_source', this.utmBuilder.utm_source.text.trim())
      }
      if (this.utmBuilder.utm_medium != null && this.utmBuilder.utm_medium.text !== '') {
        url.searchParams.set('utm_medium', this.utmBuilder.utm_medium.text.trim())
      }
      if (this.utmBuilder.utm_campaign != null && this.utmBuilder.utm_campaign.text !== '') {
        url.searchParams.set('utm_campaign', this.utmBuilder.utm_campaign.text.trim())
      }
      if (this.utmBuilder.utm_content != null && this.utmBuilder.utm_content.text !== '') {
        url.searchParams.set('utm_content', this.utmBuilder.utm_content.text.trim())
      }
      if (this.utmBuilder.customParameters.length > 0) {
      // eslint-disable-next-line no-plusplus
        for (let i = 0; i < this.utmBuilder.customParameters.length; i++) {
          const customParameter = this.utmBuilder.customParameters[i]
          // eslint-disable-next-line no-continue
          if (customParameter.name === '' || customParameter.value === '') continue

          url.searchParams.set(customParameter.name, customParameter.value.trim())
        }
      }
      this.utmUrlPreview = decodeURI(url.toString())

      if (this.isProductAd) {
        this.utmUrlPreview = this.utmUrlPreview.replace('https://', '')
      }

      this.changeInputValue(this.model)
    },
  },
}
</script>
